"use client"

import { useState, useSyncExternalStore } from "react"

import { useLocale, useTranslations } from "next-intl"

import { PaymentHeader, PlanHeader, SecuredPaymentHeader } from "@/components/marketplace/bnpl/plan-header"
import { QuincenaSplit } from "@/components/marketplace/bnpl/quincena-split"
import { Cents, Chip, DiscountBadge, HairlinePlusIcon, Surface } from "@/components/ui"
import { CloseButton } from "@/components/ui/close-button"
import { Sheet } from "@/components/ui/sheet"
import type { Locale } from "@/i18n/locales"
import { formatMoneyNarrow, formatMoneyWithCode, isPositiveMoney } from "@/lib/money"
import type { InstallmentView, PriceView } from "@/lib/money/price-view"

const WIDE_BUY_COLUMN = "(min-width: 64rem)"

const subscribeWide = (onChange: () => void) => {
  const query = window.matchMedia(WIDE_BUY_COLUMN)
  query.addEventListener("change", onChange)
  return () => query.removeEventListener("change", onChange)
}

const useWideBuyColumn = () =>
  useSyncExternalStore(
    subscribeWide,
    () => window.matchMedia(WIDE_BUY_COLUMN).matches,
    () => false
  )

interface PdpPriceHeroProps {
  price: PriceView
  className?: string
}

export const PdpPriceHero = ({ price, className }: PdpPriceHeroProps) => {
  const t = useTranslations("pdp")
  const locale = useLocale() as Locale
  const [planOpen, setPlanOpen] = useState<boolean | null>(null)
  const isWide = useWideBuyColumn()
  const installment = price.installment
  const isSecured = installment?.financingMode === "SECURED"
  const plan = isSecured || (installment?.financingMode === "REGULAR" && !(installment.downPayment && isPositiveMoney(installment.downPayment))) ? installment : null
  const firstPayment = installment?.amounts[0]
  const canOpenPlan = (plan?.dueDates.length ?? 0) > 0
  const isOpen = planOpen === true
  // One breakpoint decides the presentation: inline below the header on a wide buy
  // column, a sheet otherwise. A container query here would disagree with the sheet's
  // media query between 48rem of container and 64rem of viewport, and open both.
  const glyphRotation = isOpen && isWide ? "rotate-45" : ""

  const header = plan && firstPayment && (isSecured ? <SecuredPaymentHeader plan={plan} /> : <RegularPaymentHeader plan={plan} />)
  const sheetHeader = plan && firstPayment && (isSecured ? <SecuredPaymentHeader plan={plan} showBadge={false} /> : header)

  return (
    <div className={`flex flex-col gap-375 @3xl:gap-500 ${className ?? ""}`}>
      <div data-testid="pdp-price-hero" className="font-text flex max-w-full flex-wrap items-baseline gap-x-125 gap-y-150 @3xl:gap-x-150">
        <span className="text-fg-heading text-price-sm @3xl:text-price-md">
          <Cents>{formatMoneyWithCode(price.amount, locale)}</Cents>
        </span>
        {price.compareAt && (
          <s className="text-fg-strike text-copy @3xl:text-copy-lg strike">
            <span className="sr-only">{t("priceHero.wasPrice")} </span>
            <Cents>{formatMoneyNarrow(price.compareAt, locale)}</Cents>
          </s>
        )}
        {price.savingsPercent > 0 && (
          <DiscountBadge tone="red" size="little" className="self-center whitespace-nowrap @3xl:mt-125 @3xl:-ml-px @3xl:self-start">
            {t("priceHero.percentOff", { percent: price.savingsPercent })}
          </DiscountBadge>
        )}
      </div>

      {plan && firstPayment && (
        <Surface variant="panel" radius="none" padding="none" data-testid="pdp-plan-card" className="rounded-7-5x @3xl:rounded-10x">
          <PlanHeader canOpen={canOpenPlan} open={isOpen} onToggle={() => setPlanOpen(!isOpen)} glyphRotation={glyphRotation}>
            {header}
          </PlanHeader>
          {canOpenPlan && isWide && (
            <div data-open={isOpen ? "" : undefined} className="expand-panel">
              <div>
                <div className="px-375 pb-375 @3xl:px-500 @3xl:pb-500">
                  <QuincenaSplit plan={plan} disclaimer={t("priceHero.planDisclaimer")} />
                </div>
              </div>
            </div>
          )}
          {canOpenPlan && !isWide && (
            <Sheet open={planOpen === true} onClose={() => setPlanOpen(false)} label={t("priceHero.planSheetLabel")} solid lift handle="outside" inset="gutLoose">
              <div className="-mt-px flex items-start justify-between gap-300">
                <span className="pt-125">{sheetHeader}</span>
                <CloseButton
                  onClick={() => setPlanOpen(false)}
                  icon={<HairlinePlusIcon size={16} className="rotate-45" aria-hidden />}
                  className="text-fg-brand hover:text-fg-brand size-6"
                />
              </div>
              <QuincenaSplit plan={plan} disclaimer={t("priceHero.planDisclaimer")} className="mt-375" />
            </Sheet>
          )}
        </Surface>
      )}
    </div>
  )
}

const RegularPaymentHeader = ({ plan }: { plan: InstallmentView }) => {
  const t = useTranslations("pdp")
  const tBnpl = useTranslations("marketplace.bnpl")
  return (
    <PaymentHeader
      plan={plan}
      badge={
        plan.interestFree && (
          <Chip tone="nointerest" size="little" className="mt-150">
            {tBnpl("noInterest")}
          </Chip>
        )
      }
      caption={t("priceHero.planToday")}
    />
  )
}
